import { useRef, useState } from 'react';
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  Paragraph,
  TextField,
  useModal,
  useSnackbars,
  type PlumaManagedModalProps,
} from '@customerio/pluma-components/react';
import plur from 'plur';
import { useDeleteObjects, type DeleteObjectsSelection } from '@/services/db/object/mutations';

export type DeleteObjectsModalData = {
  objectTypeId: string;
  objectTypeName: string;
  count: number;
  selection: DeleteObjectsSelection;
};

export function DeleteObjectsModal({
  data,
  modalState,
  onClose,
  onConfirm,
}: PlumaManagedModalProps<DeleteObjectsModalData>) {
  const [confirmation, setConfirmation] = useState('');
  const isSubmitting = useRef(false);
  const snackbars = useSnackbars();
  const count = data.count;
  const deleteObjects = useDeleteObjects({
    mutationConfig: {
      onSuccess: () => {
        snackbars.create({
          id: 'objects.delete.success',
          variant: 'default',
          message: `${count} ${plur('object', count)} deleted.`,
        });
        onConfirm();
      },
      onError: () => {
        isSubmitting.current = false;
        snackbars.create({
          id: 'objects.delete.error',
          variant: 'critical',
          message: "The selected objects couldn't be deleted. Try again.",
        });
      },
    },
  });

  const submit = () => {
    if (confirmation !== String(count) || isSubmitting.current) return;
    isSubmitting.current = true;
    deleteObjects.mutate({ objectTypeId: data.objectTypeId, ...data.selection });
  };

  return (
    <Modal
      state={modalState}
      title={`Delete ${count} ${plur(data.objectTypeName, count)}?`}
      isDismissable={!deleteObjects.isPending}
    >
      <ModalBody>
        <Paragraph>
          These objects and their relationships will be permanently deleted. You can't undo this action.
        </Paragraph>
        <TextField
          label={`Type ${count} to confirm`}
          value={confirmation}
          onInput={(event) => setConfirmation(event.target.value)}
          isDisabled={deleteObjects.isPending}
        />
      </ModalBody>
      <ModalFooter>
        <Button variant="tertiary" onClick={() => onClose()} isDisabled={deleteObjects.isPending}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isCritical={true}
          isDisabled={confirmation !== String(count)}
          isLoading={deleteObjects.isPending}
          onClick={submit}
        >
          Delete
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export function useDeleteObjectsModal() {
  return useModal(DeleteObjectsModal);
}
